import TableHeader from '@/components/table/tableHeader';
import { useResponsive } from '@/hooks/use-responsive';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  InvestorDetailsCommitmentsTabTableDataQuery,
  InvestorDetailsHouseholdCommitmentsTabTableDataQuery,
  PaginateSortDirection
} from '@/__generated__/graphql';
import { Loader } from '@/components/loaders/loader';
import { StickyTableLoader } from '@/components/loaders/stickyTableLoader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapperWithLoading, TableWrapper } from '@/components/table/styles';
import TablePagination from '@/components/table/tablePagination';
import { TableColumns } from '@/components/table/types';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import useStickyFooter from '@/hooks/useStickyFooter';
import { useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import { commitmentsMobileTableSort, commitmentsTableColumns } from '../constants';
import { INVESTOR_DETAILS_COMMITMENTS_TAB_TABLE_DATA_QUERY, INVESTOR_DETAILS_HOUSEHOLD_COMMITMENTS_TAB_TABLE_DATA_QUERY } from '../queries';
import CommitmentsMobileTable from './mobileTable';
import CommitmentsTableBody from './tableBody';
import TableSumFooter from './tableSumFooter';

interface CommitmentsTableProps {
  entityType: InvestorDetailsEntityTypeEnum;
}

const CommitmentsTable = ({ entityType }: CommitmentsTableProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const { searchParams, onChangeSearch, onChangeSort, onChangeToggle, onChangePaginationData, makeScroll, saveScrollPostion } =
    useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const q = searchParams.get('q') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = searchParams.get('key') || 'name';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';
  const position = searchParams.get('position') || 0;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useStickyFooter(containerRef, footerRef);

  const [tableColumns, setTableColumns] = useState<TableColumns[]>(commitmentsTableColumns);
  const [tableRows, setTableRows] = useState<InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments']>([]);
  const [householdCommitmentsTabTableTotals, setHouseholdCommitmentsTabTableTotals] = useState<
    InvestorDetailsHouseholdCommitmentsTabTableDataQuery['investorHouseholdCommitments']['totals'] | null
  >(null);
  const [mobileSort, setMobileSort] = useState(commitmentsMobileTableSort[0].title);
  const sortItem = commitmentsMobileTableSort.find((item) => item.title === mobileSort);
  const mobileSortList = commitmentsMobileTableSort.map((item) => item.title);

  const {
    data: householdCommitmentsTabTableData,
    loading: householdCommitmentsTabTableDataLoading,
    refetch: householdCommitmentsTabTableDataRefetch
  } = useQuery(INVESTOR_DETAILS_HOUSEHOLD_COMMITMENTS_TAB_TABLE_DATA_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityType || entityType !== InvestorDetailsEntityTypeEnum.Household,
    variables: {
      data: {
        id: investorId,
        includeRecommendation: toggle,
        search: q.toLowerCase().trim(),
        sortBy: key,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        limit: Number(limit),
        offset: Number(offset)
      }
    },
    onCompleted: (data) => {
      setHouseholdCommitmentsTabTableTotals(data?.investorHouseholdCommitments?.totals || null);
    }
  });

  const {
    data: commitmentsTabTableData,
    loading: commitmentsTabTableDataLoading,
    refetch: commitmentsTabTableDataRefetch
  } = useQuery(INVESTOR_DETAILS_COMMITMENTS_TAB_TABLE_DATA_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityType || entityType === InvestorDetailsEntityTypeEnum.Household,
    variables: {
      id: entityId,
      includeRecommendation: toggle,
      search: q.toLowerCase().trim(),
      order: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
      sort: key,
      group: entityType === InvestorDetailsEntityTypeEnum.Group
    }
  });

  useEffect(() => {
    if (householdCommitmentsTabTableDataLoading || commitmentsTabTableDataLoading) return;

    if (entityType === InvestorDetailsEntityTypeEnum.Household && householdCommitmentsTabTableData) {
      setTableRows(householdCommitmentsTabTableData?.investorHouseholdCommitments?.commitments || []);
      makeScroll(position);
      return;
    }
    if (entityType !== InvestorDetailsEntityTypeEnum.Household && commitmentsTabTableData) {
      setTableRows(commitmentsTabTableData?.ClientEntityDetailsCommitments?.commitments || []);
      makeScroll(position);
      return;
    }
  }, [householdCommitmentsTabTableData, householdCommitmentsTabTableDataLoading, commitmentsTabTableData, commitmentsTabTableDataLoading]);

  useEffect(() => {
    if (!isMobile) return;

    if (entityType === InvestorDetailsEntityTypeEnum.Household) {
      householdCommitmentsTabTableDataRefetch({
        data: {
          id: investorId,
          includeRecommendation: toggle,
          search: q.toLowerCase().trim(),
          sortDirection: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
          sortBy: sortItem?.key,
          limit: Number(limit),
          offset: Number(offset)
        }
      });
      return;
    }
    commitmentsTabTableDataRefetch({
      order: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
      sort: sortItem?.key
    });
  }, [mobileSort]);

  useEffect(() => {
    if (!!entityId) {
      setTableColumns(commitmentsTableColumns);
      return;
    }
    const updatedTableColumns = commitmentsTableColumns.map((column) => {
      if (column.key === 'name') {
        return { ...column, title: 'Commitment', keySort: 'commitment' };
      }
      return column;
    });
    setTableColumns(updatedTableColumns);
  }, [entityId]);

  const openOpportunityDetailsPage = (strategyId: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${strategyId}?backToTitle=Investor Details`);
  };

  const getCurrentStatus = () => {
    if (entityType === InvestorDetailsEntityTypeEnum.Household) {
      return {
        loading: householdCommitmentsTabTableDataLoading,
        refetch: householdCommitmentsTabTableDataRefetch,
        total: householdCommitmentsTabTableData?.investorHouseholdCommitments?.total || 0
      };
    }
    return {
      loading: commitmentsTabTableDataLoading,
      refetch: commitmentsTabTableDataRefetch,
      total: tableRows?.length || 0
    };
  };

  return (
    <>
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        resultsValue={getCurrentStatus().total}
        refetch={getCurrentStatus().refetch}
        toggle={{
          title: 'Include Pending Commitments',
          isActive: toggle,
          onToggle: () => onChangeToggle(toggle, { shouldSaveScrollPosition: false })
        }}
        mobileSort={{
          value: mobileSort,
          selects: mobileSortList,
          onChange: setMobileSort
        }}
        useNewFilters
      />
      {isMobile ? (
        <>
          {getCurrentStatus().loading ? (
            <Loader />
          ) : (
            <CommitmentsMobileTable
              tableColumns={tableColumns}
              tableRows={tableRows}
              entityType={entityType}
              householdCommitmentsTabTableTotals={householdCommitmentsTabTableTotals}
              openOpportunityDetailsPage={openOpportunityDetailsPage}
            />
          )}
        </>
      ) : (
        <TableWrapper padding="0">
          <TableHeader refetch={() => {}} columns={tableColumns} savedSort={{ key, asc }} savedSetSort={onChangeSort} />
          {getCurrentStatus().loading && <StickyTableLoader />}
          <TableBodyWrapperWithLoading isLoading={getCurrentStatus().loading}>
            <CommitmentsTableBody
              tableColumns={tableColumns}
              tableRows={tableRows}
              entityType={entityType}
              openOpportunityDetailsPage={openOpportunityDetailsPage}
            />
            <TableSumFooterWrapper ref={footerRef}>
              {!!tableRows?.length && (
                <TableSumFooter
                  tableColumns={tableColumns}
                  tableRows={tableRows}
                  entityType={entityType}
                  householdCommitmentsTabTableTotals={householdCommitmentsTabTableTotals}
                />
              )}
            </TableSumFooterWrapper>
            <div ref={containerRef}></div>
          </TableBodyWrapperWithLoading>
        </TableWrapper>
      )}
      {entityType === InvestorDetailsEntityTypeEnum.Household && (
        <PaginationWrap>
          <TablePagination
            savePagination={onChangePaginationData}
            paginationValues={{
              limit: Number(limit),
              offset: Number(offset),
              total: householdCommitmentsTabTableData?.investorHouseholdCommitments?.total || 0
            }}
            refetch={() => {}}
          />
        </PaginationWrap>
      )}
    </>
  );
};

export default CommitmentsTable;

const TableSumFooterWrapper = styled.div`
  &.sticky {
    position: sticky;
    bottom: 0;
    z-index: 9;
  }
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
